/**
 * Domini dei prodotti per ambiente — UNICO posto in cui sono scritti.
 * Le Edge Functions non li duplicano: ricevono l'origine dal client (app_origin /
 * return_origin, calcolati con platform.originFor) e in mancanza usano app.mindleadsuite.com,
 * che dopo la separazione reindirizza ogni percorso al dominio del suo prodotto.
 *
 * Puro: nessun import.meta, nessun window. L'adattatore runtime è ./runtime.ts.
 */
import { PRODUCTS, isProductKey, productForPath, type ProductKey } from "./products";

export type PlatformEnv = "locale" | "collaudo" | "live";

/**
 * Host canonico di ogni prodotto per ambiente (null = nessun dominio dedicato:
 * il prodotto è raggiungibile solo dall'host "suite" tutto-in-uno).
 */
export const PRODUCT_HOSTS: Record<PlatformEnv, Record<ProductKey, string | null>> = {
  // Solo domini REALMENTE attivi su Vercel. Un prodotto senza dominio resta servito da
  // app.mindleadsuite.com come prima. Nomi previsti, da attivare quando registrati:
  // supply → app.mindleadsupply.com, crm → app.mindleadcrm.com, billing → app.mindleadfatturazione.com
  live: {
    console: "app.mindleadsuite.com",
    construction: "app.mindleadconstruction.com",
    supply: null,
    crm: null,
    billing: null,
  },
  // Il collaudo non ha (ancora) domini dedicati: anteprime Vercel e dev:collaudo sono "suite".
  collaudo: {
    console: null,
    construction: null,
    supply: null,
    crm: null,
    billing: null,
  },
  // *.localhost risolve da solo in Chrome/Firefox (Safari/Simulator: usare ?product= o VITE_PRODUCT)
  locale: {
    console: null,
    construction: "construction.localhost",
    supply: "supply.localhost",
    crm: "crm.localhost",
    billing: "billing.localhost",
  },
};

/**
 * Comportamento del dominio Console (app.mindleadsuite.com):
 * - "legacy":  serve ancora tutti i prodotti (com'era prima della separazione);
 * - "console": serve solo la Console; i percorsi degli altri prodotti vengono
 *              reindirizzati al loro dominio, conservando percorso e parametri.
 * Si cambia con la variabile VITE_SUITE_MODE su Vercel (nessuna modifica al codice).
 */
export type SuiteMode = "legacy" | "console";

/**
 * Esito della risoluzione:
 * - product: l'host appartiene a un prodotto → si monta solo quel prodotto;
 * - suite:   host tutto-in-uno (sviluppo, anteprime, Console in modalità legacy) → si monta tutto.
 */
export type HostResolution =
  | {
      mode: "product";
      product: ProductKey;
      source: "build" | "override" | "host";
      /**
       * Prodotti senza un dominio proprio che questo host continua a servire
       * (solo la Console: es. Fatturazione e CRM standalone finché non hanno un dominio).
       */
      alsoServes: ProductKey[];
    }
  | { mode: "suite" };

export interface ResolveInput {
  hostname: string;
  env: PlatformEnv;
  suiteMode: SuiteMode;
  /** Prodotto fissato in fase di build (VITE_PRODUCT, es. app iOS) */
  buildProduct?: string | null;
  /** Override esplicito, ammesso solo fuori da live (?product=) */
  override?: string | null;
}

export function productForHost(hostname: string, env: PlatformEnv): ProductKey | null {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  const table = PRODUCT_HOSTS[env];
  for (const key of Object.keys(table) as ProductKey[]) {
    if (table[key] === host) return key;
  }
  return null;
}

/** Prodotti (diversi dalla Console) che in questo ambiente non hanno ancora un dominio proprio. */
export function productsWithoutHost(env: PlatformEnv): ProductKey[] {
  return (Object.keys(PRODUCTS) as ProductKey[]).filter(
    (k) => k !== "console" && !PRODUCT_HOSTS[env][k],
  );
}

export function resolveHost(input: ResolveInput): HostResolution {
  if (isProductKey(input.buildProduct)) {
    return { mode: "product", product: input.buildProduct, source: "build", alsoServes: [] };
  }
  if (input.env !== "live" && isProductKey(input.override)) {
    return {
      mode: "product",
      product: input.override,
      source: "override",
      alsoServes: input.override === "console" ? productsWithoutHost(input.env) : [],
    };
  }
  const product = productForHost(input.hostname, input.env);
  if (!product) return { mode: "suite" };
  if (product === "console" && input.suiteMode === "legacy") return { mode: "suite" };
  return {
    mode: "product",
    product,
    source: "host",
    alsoServes: product === "console" ? productsWithoutHost(input.env) : [],
  };
}

/** Prodotti le cui route vengono montate per questa risoluzione. */
export function mountedProducts(resolution: HostResolution): ProductKey[] {
  if (resolution.mode === "suite") return Object.keys(PRODUCTS) as ProductKey[];
  return [resolution.product, ...resolution.alsoServes];
}

/** Origine (https://host) del prodotto in questo ambiente, o null se non ha un dominio dedicato. */
export function productOrigin(product: ProductKey, env: PlatformEnv, localPort = "8080"): string | null {
  const host = PRODUCT_HOSTS[env][product];
  if (!host) return null;
  if (env === "locale") return `http://${host}${localPort ? `:${localPort}` : ""}`;
  return `https://${host}`;
}

/**
 * Dove deve andare un percorso che non appartiene al prodotto montato su questo host.
 * - "here":     il percorso si serve qui;
 * - "redirect": va servito dal dominio di un altro prodotto (url completo);
 * - "notFound": non esiste su questo host (es. Console da un dominio di prodotto, o app nativa).
 */
export type PathPlacement =
  | { kind: "here" }
  | { kind: "redirect"; url: string; product: ProductKey }
  | { kind: "notFound" };

export function placePath(
  url: { pathname: string; search?: string; hash?: string },
  resolution: HostResolution,
  env: PlatformEnv,
  localPort?: string,
): PathPlacement {
  if (resolution.mode === "suite") return { kind: "here" };
  const owner = productForPath(url.pathname);
  if (!owner || mountedProducts(resolution).includes(owner)) return { kind: "here" };
  // Separazione netta: dai domini di prodotto e dalle app native la Console non si raggiunge.
  if (owner === "console" || resolution.source === "build") return { kind: "notFound" };
  const origin = productOrigin(owner, env, localPort);
  if (!origin) return { kind: "notFound" };
  return {
    kind: "redirect",
    product: owner,
    url: `${origin}${url.pathname}${url.search ?? ""}${url.hash ?? ""}`,
  };
}
